import { http, HttpResponse, delay } from 'msw';
import { faker } from '@faker-js/faker';
import { API_PREFIX, mockUrl, mockOk, mockFail, mockPaginated, readListParams } from '@/shared/api';
import type { StaffBase, StaffCreatePayload, StaffUpdatePayload } from '../model/types';

faker.seed(505);

const ADMIN = API_PREFIX.ADMIN_V1;

interface StaffRecord {
  id: number;
  full_name: string;
  phone: string;
  username: string;
  email: string;
  role_id: number;
}

const makeStaff = (id: number): StaffRecord => {
  const first = faker.person.firstName();
  const last = faker.person.lastName();
  return {
    id,
    full_name: `${first} ${last}`,
    username: faker.internet.username({ firstName: first, lastName: last }).toLowerCase(),
    email: faker.internet.email({ firstName: first, lastName: last }).toLowerCase(),
    phone: `998${faker.string.numeric(9)}`,
    role_id: faker.helpers.arrayElement([1, 2, 3]),
  };
};

const staffList: StaffRecord[] = Array.from({ length: 15 }, (_, i) => makeStaff(i + 1));
let nextId = staffList.length + 1;

const toBase = (s: StaffRecord): StaffBase => ({ ...s });

export const staffHandlers = [
  http.get(mockUrl(`${ADMIN}/staff/index`), async ({ request }) => {
    await delay();
    const { page, limit, search } = readListParams(request.url);
    const filtered = search
      ? staffList.filter((s) => `${s.full_name} ${s.username} ${s.email}`.toLowerCase().includes(search))
      : staffList;
    return HttpResponse.json(mockPaginated(filtered.map(toBase), page, limit));
  }),

  http.get(mockUrl(`${ADMIN}/staff/show`), async ({ request }) => {
    await delay();
    const id = Number(new URL(request.url).searchParams.get('id'));
    const staff = staffList.find((s) => s.id === id);
    if (!staff) return HttpResponse.json(mockFail('Staff not found'), { status: 404 });
    return HttpResponse.json(mockOk(toBase(staff)));
  }),

  http.post(mockUrl(`${ADMIN}/staff/create`), async ({ request }) => {
    await delay();
    const payload = (await request.json()) as StaffCreatePayload;
    const staff: StaffRecord = {
      id: nextId++,
      full_name: payload.full_name,
      phone: payload.phone,
      username: payload.username,
      email: payload.email ?? '',
      role_id: payload.role_id,
    };
    staffList.unshift(staff);
    return HttpResponse.json(mockOk(toBase(staff)), { status: 201 });
  }),

  http.post(mockUrl(`${ADMIN}/staff/update`), async ({ request }) => {
    await delay();
    const payload = (await request.json()) as StaffUpdatePayload;
    const staff = staffList.find((s) => s.id === payload.id);
    if (!staff) return HttpResponse.json(mockFail('Staff not found'), { status: 404 });
    Object.assign(staff, {
      full_name: payload.full_name,
      phone: payload.phone,
      email: payload.email ?? staff.email,
      role_id: payload.role_id,
    });
    return HttpResponse.json(mockOk(toBase(staff)));
  }),

  http.post(mockUrl(`${ADMIN}/staff/delete`), async ({ request }) => {
    await delay();
    const { id } = (await request.json()) as { id: number };
    const index = staffList.findIndex((s) => s.id === id);
    if (index === -1) return HttpResponse.json(mockFail('Staff not found'), { status: 404 });
    staffList.splice(index, 1);
    return HttpResponse.json(mockOk(null));
  }),

  http.post(mockUrl(`${ADMIN}/staff/reset-password`), async ({ request }) => {
    await delay();
    const { staff_id } = (await request.json()) as { staff_id: number };
    const staff = staffList.find((s) => s.id === staff_id);
    if (!staff) return HttpResponse.json(mockFail('Staff not found'), { status: 404 });
    return HttpResponse.json(mockOk(null));
  }),
];
